
import { jitsiLocalStorage } from '@jitsi/js-utils';
import Logger from 'meet-hour-logger';
import React, { PureComponent } from 'react';
import { createPortal } from 'react-dom';

import { stateType } from '../../app/stateTypes';
import { normalizeAudioLevel } from '../../audio-level-indicator/functions';
import Avatar from '../../base/avatar/components/Avatar';
import { getCurrentConference } from '../../base/conference/functions';
import { setPictureInPictureModeConfig } from '../../base/config';
import { getPictureInPictureModeConfig } from '../../base/config/functions.any';
import { hideDialog, openDialog } from '../../base/dialog';
import { isDeviceLandScape } from '../../base/environment/environment';
import { isMobileBrowser } from '../../base/environment/utils';
import { translate } from '../../base/i18n';
import MeetHourJS, { browser } from '../../base/lib-meet-hour';
import {
    CAMERA_ZOOM_MAX,
    CAMERA_ZOOM_MIN,
    MEDIA_TYPE,
    VIDEO_TYPE,
    setCameraZoom,
    setScreenshareZoom
} from '../../base/media';
import {
    getLocalParticipant,
    getParticipantById,
    getParticipantDisplayName,
    getScreenSharingParticipantId,
    getVirtualScreenshareParticipantOwnerId,
    isLocalParticipantModerator,
    pinParticipant
} from '../../base/participants';
import { connect } from '../../base/redux';
import {
    getLocalAudioTrack,
    getTrackByMediaTypeAndParticipant,
    isLocalTrackMuted,
    isRemoteTrackMuted
} from '../../base/tracks';
import DraggableResizable from '../../base/ui/components/web/DraggableResizable';
import { appendPreservingState } from '../../base/util/pipDomAdoption';
import { copyStylesToPipWindow, watchStyleChanges } from '../../base/util/pipStyleUtils';
import Labels from '../../conference/components/web/Labels';
import ConnectionLostOverlay from '../../connection-indicator/components/web/ConnectionLostOverlay';
import LargeVideoDisplayName from '../../display-name/components/web/DisplayNameLabel';
import { fetchCustomBrandingData } from '../../dynamic-branding';
import { setHideHeaderFooter } from '../../filmstrip/actions.web';
import {
    LocalVideoContextMenu,
    showLocalVideoContextMenu
} from '../../filmstrip/components/web/LocalVideoContextMenu';
import {
    RemoteVideoContextMenu,
    showRemoteVideoContextMenu
} from '../../filmstrip/components/web/RemoteVideoContextMenu';
import Thumbnail from '../../filmstrip/components/web/Thumbnail';
import { GenericIFrameManager } from '../../genericiframe/components/index.web';
import {
    getGifDisplayMode,
    getGifForParticipant
} from '../../gifs/function.any';
import PrismaZoom from '../../react-zoom/index';
import ReactionAnimations from '../../reactions/components/web/ReactionsAnimations';
import ScreenAnnotationOverlay from '../../screen-annotation/components/web/ScreenAnnotationOverlay';
import { startScreenShareFlow } from '../../screen-share/actions.web';
import SharedVideo from '../../shared-video/components/web/SharedVideo';
import { YOUTUBE_PLAYER_PARTICIPANT_NAME } from '../../shared-video/constants';
import { isYouTubePlaying } from '../../shared-video/functions';
import { setLabelsVisible } from '../../toolbox/actions.web';
import { Toolbox } from '../../toolbox/components/web';
import { LAYOUTS, VideoLayout, shouldDisplayTileView } from '../../video-layout';
import { muteLocal } from '../../video-menu/actions.any';
import { iAmVisitor } from '../../visitors/functions';
import {
    getWebinarAudienceLargeVideoParticipants,
    isAudienceStageOnlyModeActive,
    isPresenterHiddenForLocal
} from '../../webinar/functions';
import {
    DOUBLE_CLICK_VIEW_ZOOM,
    IDENTITY_ZOOM_STATE,
    ZOOM_SYNC_EPSILON,
    ZOOM_SYNC_SEND_INTERVAL_MS,
    ZoomShareDialog,
    flipZoomStateX,
    getLocalVideoZoom,
    getLocalZoomShareApproved,
    getModeratorZoomShareApproved,
    getRemoteVideoZoom,
    getRequestedViewZoom,
    getStageVideo,
    isSameZoomState,
    localVideoZoomChanged,
    remoteVideoZoomChanged,
    setLocalZoomShareApproved,
    setModeratorZoomShareApproved,
    toSyncState,
    toTransform
} from '../../zoom-sync';
import { selectParticipantInLargeVideo } from '../actions.any';

import LargeVideoElement from './LargeVideoElement';

// import { selectParticipantInLargeVideo } from '../actions.any';

const MHTrackEvents = MeetHourJS.events.track;
const logger = Logger.getLogger(__filename);

/**
 * How long to wait for the large video element to have metadata before giving up on entering Picture-in-Picture.
 */
const PIP_METADATA_TIMEOUT_MS = 5000;

/**
 * The application root that document Picture-in-Picture moved out of the main document, and where it came from.
 *
 * Kept at module level rather than on the component: the root has to come back even if the LargeVideo instance
 * that moved it has been unmounted and replaced in the meantime, and the replacement has no other way to find
 * it - a root sitting in a PiP document (or in one whose window has already closed) is invisible to
 * `document.querySelector('#react')`.
 *
 * @type {{ root: Element, home: Element }|null}
 */
let pipAdoption = null;

/**
 * Moves the application root back into the main document and removes the "Exit Picture-in-Picture" cover.
 *
 * Returning the conference used to happen in one place only: the PiP window's `pagehide` listener. Every other
 * way out - the cover's Exit button, the PiP label, the redux state going false - just closed the window and
 * relied on that listener. When the window was already gone, which is what switching tabs quickly does to the
 * automatic PiP window on a slow machine (Chrome closes it as soon as the tab is shown again), there was no
 * `pagehide` left to fire, so the root stayed in a dead document and the user was left looking at the Exit
 * button with nothing behind it, no matter how often they clicked it. This does not depend on the window at all:
 * it moves the root back from wherever it is. A held reference is enough - a node can be adopted back out of a
 * document whose window has closed.
 *
 * Safe to call any number of times.
 *
 * @param {Document} [onlyFrom] - Restore only if the root currently lives in this document. The `pagehide` of a
 * superseded window passes its own document, so it cannot pull the root out of a newer window that took it.
 * @returns {void}
 */
function restorePipAdoption(onlyFrom) {
    if (pipAdoption) {
        const { root, home } = pipAdoption;

        if (onlyFrom && root.ownerDocument !== onlyFrom) {
            return;
        }

        // Not `root.isConnected`: a root left in the document of a closed window still reports connected.
        if (!document.contains(root)) {
            appendPreservingState(document.contains(home) ? home : document.body, root);
        }

        pipAdoption = null;
    }

    document.querySelectorAll('#pip-overlay').forEach(overlay => overlay.remove());
}

/**
 * The "Loading..." cover drawn over the stage while it has no participant to show. It covers the stage the way the
 * separate tree it replaces did, above everything the stage draws (the connection-lost overlay reaches 1002).
 */
const LOADING_OVERLAY_STYLE = {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: 1003,
    backgroundColor: '#000',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
};

const LOADING_TEXT_STYLE = {
    color: '#fff',
    fontSize: '16px'
};

// global interfaceConfig

type Props = {

  /**
   * The user selected background color.
   */
  _customBackgroundColor: string,

  /**
   * The audio track related to the participant.
   */
  _audioTrack: ?Object,

  /**
   * The user selected background image url.
   */
  _customBackgroundImageUrl: string,

  /**
   * Fetches the branding data.
   */
  _fetchCustomBrandingData: Function,

  /**
   * Prop that indicates whether the chat is open.
   */
  _isChatOpen: boolean,

  /**
   * Used to determine the value of the autoplay attribute of the underlying
   * video element.
   */
  _noAutoPlayVideo: boolean,

  /**
   * Used to Increase the number of the on click.
   */
  _clickbutton: Number,

  /**
   * Used to Increase the number of the on click.
   */
  _clickbutton1: Number,

  /**
   * Capture zoom of the local camera, as stored in redux.
   */
  _cameraZoom: number,

  /**
   * 'camera' or 'desktop' while, in a mobile browser, the large video shows
   * a local capture whose zoom a pinch should change; undefined otherwise.
   */
  _captureZoomType?: string,

  /**
   * View zoom the local participant last shared for the video on stage, when
   * that is their own camera or screenshare.
   */
  _localVideoZoom?: Object,

  /**
   * View zoom the remote participant on stage applied to their own video.
   */
  _remoteVideoZoom?: Object,

  /**
   * Whether the local self view is mirrored (settings). Only watched so a
   * shared zoom is re-sent when the mirroring changes underneath it.
   */
  _localFlipX?: boolean,

  /**
   * Pending zoom level request from the 1x / 2x / ... button.
   */
  _requestedViewZoom?: Object,

  /**
   * Capture zoom of the local screenshare, as stored in redux.
   */
  _screenshareZoom: number,

  /**
   * Object fit the large video element renders with; the sync math needs it
   * to map between box and picture coordinates.
   */
  _stageObjectFit: string,

  /**
   * Owner, type and locality of the video on stage.
   */
  _stageVideo?: Object,

  /**
   * Used to _gifSrc.
   */
  _gifSrc: string,

  /**
   * Used to _isPictureInPictureVisible.
   */
  _isPictureInPictureVisible: boolean,

  /**
   * Used to _shouldDisplayTileView.
   */
  _shouldDisplayTileView: boolean,

  /**
   * Used to _videoTrack.
   */
  _videoTrack: Object,

  /**
   * Used to find if a participant has joined the call
   */
  _mhConferenceJoined: boolean,

    /**
     * The connection status of the local participant.
     */
    _audioTrack: ?Object,

    /**
     * The user selected background image url.
     */
    _customBackgroundImageUrl: string,

    /**
     * Fetches the branding data.
     */
    _fetchCustomBrandingData: Function,

    /**
     * Prop that indicates whether the chat is open.
     */
    _isChatOpen: boolean,

    /**
     * Prop that indicates whether the app is running on a desktop platform.
     */
    isDesktop: boolean,

    /**
     * Used to determine the value of the autoplay attribute of the underlying
     * video element.
     */
    _noAutoPlayVideo: boolean,

    /**
     * Used to Increase the number of the on click.
     */
    _clickbutton: Number,

    /**
     * Used to Increase the number of the on click.
     */
    _clickbutton1: Number,

    /**
     * Used to _gifSrc.
     */
    _gifSrc: string,

    /**
     * Used to _isPictureInPictureVisible.
     */
    _isPictureInPictureVisible: boolean,

    /**
     * Used to _shouldDisplayTileView.
     */
    _shouldDisplayTileView: boolean,

    /**
     * Used to _videoTrack.
     */
    _videoTrack: Object,

    /**
     * Used to find if a participant has joined the call
     */
    _mhConferenceJoined: boolean,

    _pinParticipant: Function,

    /**
     * Used to disable auto PiP.
     */
    userDisabledAutoPiP: boolean,

    /**
     * Whether sharing of local zoom is approved.
     */
    _localZoomShareApproved?: boolean,

    /**
     * Whether sharing of moderator pin zoom is approved.
     */
    _moderatorZoomShareApproved?: boolean,

    _iAmVisitor?: boolean,

    _localParticipant?: Object
};

type State = {
  zoom?: any,
};

/**
 * Whether document Picture-in-Picture can be used here. The API is exposed inside iframes as well, but
 * requestWindow() only works from a top-level browsing context and throws NotAllowedError anywhere else (e.g. when
 * the conference is embedded or opened through a proxy that frames it), so framed pages use the classic video
 * element PiP instead.
 *
 * @returns {boolean}
 */
function _canUseDocumentPictureInPicture() {
    return 'documentPictureInPicture' in window && window.self === window.top;
}

/** .
 * Implements a React {@link Component} which represents the large video (a.k.a.
 * The conference participant who is on the local stage) on Web/React.
 *
 * @augments Component
 */
class LargeVideo extends PureComponent<Props, State> {
    /**
   * Constructor for LargeVideo Component.
   * @param {*} props
   */
    constructor(props) {
        super(props);
        this.state = {
            audioLevel: 0,
            disablepipdialog: false,
            prevPinParticipant: null,
            showMyScreenAnyway: jitsiLocalStorage.getItem('show_screen_while_screensharing') === 'true'
        };
        this.mainVideoRef = React.createRef();
        this.prismaZoomRef = React.createRef();
        this._currentAudioLevelTrack = null;
        this._audioLevelTimeout = null;
        this._updateAudioLevel = this._updateAudioLevel.bind(this);
        this._handleContextMenu = this._handleContextMenu.bind(this);
        this._onHandleHeaderFooter = this._onHandleHeaderFooter.bind(this);
        this._onShowMyScreenChanged = ({ detail }) => this.setState({ showMyScreenAnyway: detail });
        this._onVisibilityChangeForPip = this._onVisibilityChangeForPip.bind(this);
        this._onLeavePictureInPicture = this._onLeavePictureInPicture.bind(this);
    }

    _onLeavePictureInPicture() {
        this.props._setPictureInPictureModeConfig(false);
    }

    pipWindow = null;
    styleWatcherCleanup = null;
    _pipAudioObserver = null;

    /**
     * Incremented by every PiP enter and exit request.
     *
     * Entering PiP has to await requestWindow() and then the stylesheet copy, and PiP is toggled from
     * componentDidUpdate on a Redux prop - so an exit can land in the middle of an in-flight enter and
     * null out (and close) the very window that enter is still setting up. Comparing the id captured at
     * the start of an enter against this lets that enter notice it has been superseded and stop, instead
     * of dereferencing a window that no longer exists.
     *
     * @type {number}
     */
    _pipSessionId = 0;

    /**
     * Cache for whether sharing of local zoom with all participants is approved.
     * @type {boolean|undefined}
     */
    _localZoomShareApproved = undefined;

    /**
     * Cache for whether sharing of moderator pin zoom with all participants is approved.
     * @type {boolean|undefined}
     */
    _moderatorZoomShareApproved = undefined;

    /**
     * Whether the ZoomShareDialog is currently open.
     * @type {boolean}
     */
    _isZoomConfirmationDialogOpen = false;

    /**
     * Pending zoom transform awaiting confirmation dialog response.
     * @type {Object|null}
     */
    _pendingZoomTransform = null;

    /**
     * Flag indicating if the moderator is actively controlling the zoom.
     * @type {boolean}
     */
    _isActivelyControllingZoom = false;

    /**
   * Implements React's {@link Component#componentDidUpdate}.
   *
   * @inheritdoc
   */
    componentDidUpdate(prevProps) {
        const { _participantId, _participants, _audioTrack, isLocalParticipantScreenSharing } = this.props;

        this._updateStageZoom(prevProps);
        this._reelectMissingParticipant();

        if (this.props._localZoomShareApproved !== prevProps._localZoomShareApproved) {
            this._localZoomShareApproved = this.props._localZoomShareApproved;
            if (this.props._localZoomShareApproved && this._isViewZoomShared() && this.prismaZoomRef.current) {
                const transform = this.prismaZoomRef.current.getTransform();

                if (transform && transform.zoom > 1 + ZOOM_SYNC_EPSILON) {
                    this._broadcastZoom(transform);
                }
            } else if (!this.props._localZoomShareApproved && this.props._stageVideo?.local) {
                this.props.dispatch(localVideoZoomChanged(this.props._stageVideo.videoType, IDENTITY_ZOOM_STATE));
            }
        }

        if (this.props._moderatorZoomShareApproved !== prevProps._moderatorZoomShareApproved) {
            this._moderatorZoomShareApproved = this.props._moderatorZoomShareApproved;
            if (this.props._moderatorZoomShareApproved && this._isViewZoomShared() && this.prismaZoomRef.current) {
                const transform = this.prismaZoomRef.current.getTransform();

                if (transform && transform.zoom > 1 + ZOOM_SYNC_EPSILON) {
                    this._broadcastZoom(transform);
                }
            } else if (!this.props._moderatorZoomShareApproved && this.props._stageVideo) {
                if (this._isViewZoomShared()) {
                    this._sendModeratorZoom(IDENTITY_ZOOM_STATE, this.props._stageVideo);
                    this.props.dispatch(remoteVideoZoomChanged(this.props._stageVideo.participantId, this.props._stageVideo.videoType, IDENTITY_ZOOM_STATE));
                } else if (this.prismaZoomRef.current) {
                    this.prismaZoomRef.current.reset();
                }
            }
        }

        // Handle participant or audio track changes
        const participantChanged = prevProps._participantId !== _participantId;
        const audioTrackChanged = prevProps._audioTrack !== _audioTrack;
        const muteStatusChanged = prevProps._showAudioMutedIndicator !== this.props._showAudioMutedIndicator
            || prevProps._audioTrack?.muted !== _audioTrack?.muted;

        if (participantChanged || audioTrackChanged) {
            this._stopListeningForAudioUpdates(prevProps._audioTrack);
            this._listenForAudioUpdates();
            this._updateAudioLevel(0);
        } else if (muteStatusChanged && (this.props._showAudioMutedIndicator || _audioTrack?.muted)) {
            this._updateAudioLevel(0);
        }

        if (isLocalParticipantScreenSharing && prevProps.isLocalParticipantScreenSharing !== isLocalParticipantScreenSharing) {
            this.setState({ showMyScreenAnyway: false });
        }

        // Selecting the moderator-pinned participant is now handled centrally: the
        // SET_MODERATOR_PIN action forces a large-video selection in the large-video
        // of the election hierarchy.


        // Handle zoom in functionality
        if (prevProps._clickbutton !== this.props._clickbutton && this.props._clickbutton > 0) {

            if (this.prismaZoomRef.current && this.prismaZoomRef.current.zoomIn) {
                this.prismaZoomRef.current.zoomIn(0.2); // Use smaller increment for smoother zoom
            }
        }

        // Handle zoom out functionality
        if (prevProps._clickbutton1 !== this.props._clickbutton1 && this.props._clickbutton1 >= 0) {

            if (this.prismaZoomRef.current && this.prismaZoomRef.current.zoomOut) {
                this.prismaZoomRef.current.zoomOut(0.2); // Use smaller increment for smoother zoom
            }
        }

        try {
            // Request video to automatically enter picture-in-picture when eligible.
            if (_canUseDocumentPictureInPicture()) {
                if (this.props._mhConferenceJoined) {
                    navigator.mediaSession.setActionHandler('enterpictureinpicture', () => {
                        const shouldDisableAutoPiP = this.props.userDisabledAutoPiP || this.state.disablepipdialog;

                        // Only prevent auto PiP if local user is sharing YouTube
                        if (!shouldDisableAutoPiP && !this.props._isLocalUserSharingYouTube) {
                            this.props._setPictureInPictureModeConfig(true);
                        }
                    });
                }
            }
        } catch {
            logger.log('The enter picture-in-picture action is not yet supported.');
        }

        if (
            this.props._isPictureInPictureVisible
            && this.props._isPictureInPictureVisible !== prevProps._isPictureInPictureVisible && !this.props.isDesktop
        ) {
            // Only prevent entering PiP if local user is sharing YouTube
            if (this.props._isLocalUserSharingYouTube) {
                logger.log(
          'Preventing Picture-in-Picture entry because local user is sharing YouTube'
                );
                this.props._setPictureInPictureModeConfig(false);
            } else if (!browser.isSafari()) {
                this._enterPictureInPicture();
            }
        } else if (
            this.props._isPictureInPictureVisible === false
      && prevProps._isPictureInPictureVisible === true
        ) {
            if (!browser.isSafari()) {
                this._exitPictureInPicture();
            }
        }

        // Validate participant state during updates
        if (_participantId && _participants) {
            const participant = _participants.find(p => p?.id === _participantId);

            if (!participant) {
                logger.warn(
          `LargeVideo: Participant ${_participantId} not found in participants list during update`
                );
            }
        }
    }

    /**
   * LargeVideo ComponentDidMount
   */
    componentDidMount() {
        const mainVideo = this.mainVideoRef.current;

        this.props._fetchCustomBrandingData();

        window.addEventListener('show-my-screen-changed', this._onShowMyScreenChanged);
        document.addEventListener('visibilitychange', this._onVisibilityChangeForPip);

        if (mainVideo) {
            mainVideo.addEventListener('leavepictureinpicture', this._onLeavePictureInPicture);
            mainVideo.addEventListener('click', this._onHandleHeaderFooter);
        }
        this._listenForAudioUpdates();

        // Add context menu event listener to large video container
        const largeVideoContainer = document.getElementById('largeVideoContainer');

        if (largeVideoContainer) {
            largeVideoContainer.addEventListener(
        'contextmenu',
        this._handleContextMenu
            );
        }

        // A shared zoom is stored normalized; its pixel transform depends on
        // the size of this box, so it is redone whenever the box changes.
        const stageBox = this.prismaZoomRef.current?.layoutRef?.current;

        if (stageBox && typeof ResizeObserver === 'function') {
            this._stageResizeObserver = new ResizeObserver(this._onStageResize);
            this._stageResizeObserver.observe(stageBox);
        } else {
            window.addEventListener('resize', this._onStageResize);
        }

        this._applyStageZoom();
        this._reelectMissingParticipant();
    }

    /**
     * Puts someone on stage when the selected participant is not in the participant list - nothing selected yet, or
     * someone who has left. render() used to do this by dispatching while rendering.
     *
     * @private
     * @returns {void}
     */
    _reelectMissingParticipant() {
        const { _filmStripView, _participantId, _participants = [], dispatch } = this.props;

        if (_filmStripView === LAYOUTS.TILE_VIEW || _participants.find(p => p?.id === _participantId)) {
            this._lastReelection = undefined;

            return;
        }

        const fallback = _participants.find(p => p?.pinned)
            || _participants.find(p => p?.dominantSpeaker && !p?.local)
            || _participants.filter(p => !p?.local && !p?.botType).pop()
            || _participants.find(p => !p?.botType)
            || _participants[0];
        const last = this._lastReelection;

        // Once per missing id and participant list: render() used to retry on every render, and this component
        // re-renders for unrelated reasons (audio levels) - so a selection that does not take (Focus Mode can refuse
        // it) is retried when the participants change, which is what could make it succeed, and not otherwise.
        if (fallback
                && (last?.participantId !== _participantId || last?.participants !== _participants)) {
            this._lastReelection = {
                participantId: _participantId,
                participants: _participants
            };
            dispatch(selectParticipantInLargeVideo(fallback.id));
        }
    }

    /**
   * Unsubscribe from audio level updates.
   *
   * @inheritdoc
   * @returns {void}
   */
    componentWillUnmount() {
        window.removeEventListener('show-my-screen-changed', this._onShowMyScreenChanged);

        if (this._audioLevelTimeout) {
            clearTimeout(this._audioLevelTimeout);
            this._audioLevelTimeout = null;
        }

        this._stopListeningForAudioUpdates(this.props._audioTrack);

        this._cleanupPipAudioObserver();

        document.removeEventListener('visibilitychange', this._onVisibilityChangeForPip);

        if (this._stageResizeObserver) {
            this._stageResizeObserver.disconnect();
            this._stageResizeObserver = null;
        } else {
            window.removeEventListener('resize', this._onStageResize);
        }
        if (this._stageResizeFrame) {
            cancelAnimationFrame(this._stageResizeFrame);
            this._stageResizeFrame = null;
        }

        const mainVideo = this.mainVideoRef.current;

        if (mainVideo) {
            mainVideo.removeEventListener('leavepictureinpicture', this._onLeavePictureInPicture);
            mainVideo.removeEventListener('click', this._onHandleHeaderFooter);
            mainVideo.removeEventListener('leavepictureinpicture', this._onLeavePictureInPicture);
        }

        // Clean up style watcher if active
        if (this.styleWatcherCleanup) {
            this.styleWatcherCleanup();
            this.styleWatcherCleanup = null;
        }

        // Remove context menu event listener
        const largeVideoContainer = document.getElementById('largeVideoContainer');

        if (largeVideoContainer) {
            largeVideoContainer.removeEventListener(
        'contextmenu',
        this._handleContextMenu
            );
        }

        if (this._moderatorZoomTimer) {
            clearTimeout(this._moderatorZoomTimer);
            this._moderatorZoomTimer = null;
        }

        if (this._isZoomConfirmationDialogOpen) {
            this._isZoomConfirmationDialogOpen = false;
            this.props.dispatch(hideDialog(ZoomShareDialog));
        }
    }

    /**
   * Starts listening for audio level updates from the library.
   *
   * @private
   * @returns {void}
   */
    _listenForAudioUpdates() {
        const { _audioTrack } = this.props;

        if (_audioTrack) {
            const { mhTrack } = _audioTrack;

            if (!mhTrack || this._currentAudioLevelTrack === mhTrack) {
                return;
            }

            if (this._currentAudioLevelTrack) {
                this._currentAudioLevelTrack.off(
                    MHTrackEvents.TRACK_AUDIO_LEVEL_CHANGED,
                    this._updateAudioLevel
                );
            }

            mhTrack.on(
                MHTrackEvents.TRACK_AUDIO_LEVEL_CHANGED,
                this._updateAudioLevel
            );

            this._currentAudioLevelTrack = mhTrack;
        }
    }

    /**
   * Stops listening to further updates from the passed track.
   *
   * @param {Object} [audioTrack] - The track.
   * @private
   * @returns {void}
   */
    _stopListeningForAudioUpdates(audioTrack) {
        if (this._audioLevelTimeout) {
            clearTimeout(this._audioLevelTimeout);
            this._audioLevelTimeout = null;
        }

        const trackToStop = audioTrack?.mhTrack || this._currentAudioLevelTrack;

        if (trackToStop) {
            trackToStop.off(
                MHTrackEvents.TRACK_AUDIO_LEVEL_CHANGED,
                this._updateAudioLevel
            );

            if (this._currentAudioLevelTrack === trackToStop) {
                this._currentAudioLevelTrack = null;
            }
        }
    }

    /**
   * Updates the internal state of the last known audio level. The level should
   * be between 0 and 1, as the level will be used as a percentage out of 1.
   *
   * @param {number} audioLevel - The new audio level for the track.
   * @private
   * @returns {void}
   */
    _updateAudioLevel(audioLevel) {
        if (this._audioLevelTimeout) {
            clearTimeout(this._audioLevelTimeout);
            this._audioLevelTimeout = null;
        }

        const isMuted = this.props._showAudioMutedIndicator || Boolean(this.props._audioTrack?.muted);
        let level = 0;

        if (!isMuted) {
            level = Math.min(normalizeAudioLevel(audioLevel), 1);
        }

        // Every setState re-renders the entire stage, so skip the ones that would draw the same level.
        if (level !== this.state.audioLevel) {
            this.setState({
                audioLevel: level
            });
        }

        // Decay back to 0 if speech stops or DTX suppresses transmission
        if (level > 0) {
            this._audioLevelTimeout = setTimeout(() => {
                this.setState({ audioLevel: 0 });
            }, 300);
        }
    }

    /**
     * @param {boolean} hideHeaderFooter - true/false to hide header and footer.
     * @returns {{
     *     hideHeaderFooter: boolean
     * }}
     */
    _onHandleHeaderFooter() {
        if (isDeviceLandScape()) {
            this.props.dispatch(setHideHeaderFooter(!this.props.hideHeaderFooter));
            this.props.dispatch(setLabelsVisible(this.props.hideHeaderFooter));
        }
    }


    _onZoomChange = zoom => this.setState({ zoom });

    /**
     * Key identifying the video on stage for zoom purposes.
     *
     * @param {Object} stageVideo - The stage video descriptor.
     * @returns {string}
     */
    _stageKey = stageVideo => {
        if (!stageVideo) {
            return '';
        }

        return `${stageVideo.participantId}/${stageVideo.videoType}`;
    };

    /**
     * Size of the untransformed large video box.
     *
     * @returns {{ width: number, height: number }}
     */
    _getStageBox = () => {
        const box = this.prismaZoomRef.current?.layoutRef?.current;

        return {
            width: box?.offsetWidth || 0,
            height: box?.offsetHeight || 0
        };
    };

    /**
     * Width / height of the picture in the large video element.
     *
     * @returns {number}
     */
    _getStageAspectRatio = () => {
        const video = this.mainVideoRef.current;

        return video?.videoWidth > 0 && video?.videoHeight > 0
            ? video.videoWidth / video.videoHeight
            : 16 / 9;
    };

    /**
     * Desktop browsers share the view zoom of the local user's own video with
     * everyone else. Mobile browsers zoom the capture itself instead, and the
     * preview must not be transformed on top of that.
     *
     * @returns {boolean}
     */
    /**
     * Desktop browsers share the view zoom of the local user's own video with
     * everyone else, or allow a moderator to share the view zoom of a moderator-pinned
     * remote participant. Mobile browsers zoom the capture itself instead, and the
     * preview must not be transformed on top of that.
     *
     * @returns {boolean}
     */
    _isViewZoomShared = () => {
        if (isMobileBrowser() || !this.props._stageVideo) {
            return false;
        }

        if (this.props._stageVideo.local) {
            return true;
        }

        if (this.props._isModerator) {
            return true;
        }

        return false;
    };

    /**
     * Whether the large video element is drawn mirrored right now - the local
     * self view usually is (scaleX(-1) set by VideoContainer). Read from the
     * rendered style rather than from settings so it always matches what the
     * user actually sees, whichever code path applied it.
     *
     * @returns {boolean}
     */
    _isStageVideoMirrored = () => {
        const video = this.mainVideoRef.current;

        if (!video) {
            return false;
        }

        const transform = window.getComputedStyle(video).transform || '';
        const matrix = transform.match(/^matrix\(\s*(-?[\d.]+)/);

        return matrix ? parseFloat(matrix[1]) < 0 : /scaleX\(\s*-1|scale\(\s*-1/.test(video.style.transform || '');
    };

    /**
     * Returns whether zoom sharing was approved for the active zoom case.
     *
     * @param {boolean} isModeratorPinnedRemote - Whether this is a moderator zooming a pinned remote participant.
     * @private
     * @returns {boolean|undefined}
     */
    _getZoomShareApproved = isModeratorPinnedRemote => {
        if (isModeratorPinnedRemote) {
            if (this._moderatorZoomShareApproved !== undefined) {
                return this._moderatorZoomShareApproved;
            }

            return this.props._moderatorZoomShareApproved;
        }

        if (this._localZoomShareApproved !== undefined) {
            return this._localZoomShareApproved;
        }

        return this.props._localZoomShareApproved;
    };

    /**
     * Called by PrismaZoom whenever the large video transform changes: double
     * click, wheel, drag, the +/- buttons or the level button. When the video
     * on stage is the local user's own camera or screenshare, or a moderator-pinned
     * participant zoomed by a moderator, asks the user via a confirmation dialog
     * before sharing the zoomed view with all participants. Asks only once per
     * zoom case (local zoom vs moderator pin zoom).
     *
     * @param {Object} transform - { zoom, posX, posY }.
     * @returns {void}
     */
    _onTransformChange = transform => {
        if (this._isApplyingStageZoom) {
            this._isApplyingStageZoom = false;

            return;
        }

        const { _stageVideo, dispatch, _isModerator } = this.props;

        const box = this._getStageBox();

        // A hidden / collapsing box has no geometry to normalize against;
        // sharing from it would reset the others to 1x for no reason.
        if (!this._isViewZoomShared() || !(box.width > 0) || !(box.height > 0)) {
            return;
        }

        const isModeratorPinnedRemote = Boolean(_isModerator && !_stageVideo.local);
        const zoomShareApproved = this._getZoomShareApproved(isModeratorPinnedRemote);
        const isZoomed = transform && transform.zoom > 1 + ZOOM_SYNC_EPSILON;

        // View has returned to normal (1x)
        if (!isZoomed) {
            if (zoomShareApproved) {
                if (_stageVideo.local) {
                    dispatch(localVideoZoomChanged(_stageVideo.videoType, IDENTITY_ZOOM_STATE));
                } else if (_isModerator) {
                    this._sendModeratorZoom(IDENTITY_ZOOM_STATE);
                    dispatch(remoteVideoZoomChanged(_stageVideo.participantId, _stageVideo.videoType, IDENTITY_ZOOM_STATE));
                }
            }

            // Do not close the zoom confirmation dialog if it is open awaiting user choice.
            if (this._isZoomConfirmationDialogOpen) {
                this._pendingZoomTransform = transform;

                return;
            }

            this._pendingZoomTransform = null;
            this._isActivelyControllingZoom = false;

            return;
        }

        // The view is zoomed (> 1x)
        this._isActivelyControllingZoom = true;

        // Before showing to all participants, ask in a dialog (ask only once)
        if (zoomShareApproved === undefined) {
            this._pendingZoomTransform = transform;

            if (!this._isZoomConfirmationDialogOpen) {
                this._isZoomConfirmationDialogOpen = true;

                const isVideoTypeScreenshare = _stageVideo.videoType === VIDEO_TYPE.DESKTOP;

                dispatch(openDialog(ZoomShareDialog, {
                    isModeratorPinnedRemote,
                    isVideoTypeScreenshare,
                    onConfirm: () => this._onConfirmShareZoom(isModeratorPinnedRemote),
                    onCancel: () => this._onDeclineShareZoom(isModeratorPinnedRemote)
                }));
            }

            return;
        }

        // If user chose NOT to share, keep zoom purely local for this viewer
        if (zoomShareApproved === false) {
            return;
        }

        // User approved sharing with everyone: broadcast it
        this._broadcastZoom(transform);
    };

    /**
     * Broadcasts the normalized zoom state to other participants.
     *
     * @param {Object} transform - { zoom, posX, posY }.
     * @private
     * @returns {void}
     */
    _broadcastZoom = transform => {
        const { _localVideoZoom, _stageObjectFit, _stageVideo, dispatch, _isModerator } = this.props;
        const box = this._getStageBox();

        if (!(box.width > 0) || !(box.height > 0) || !transform || !_stageVideo) {
            return;
        }

        let zoomState = toSyncState(transform, box, this._getStageAspectRatio(), _stageObjectFit);

        if (this._isStageVideoMirrored()) {
            zoomState = flipZoomStateX(zoomState);
        }

        if (_stageVideo.local) {
            if (!isSameZoomState(_localVideoZoom, zoomState)) {
                dispatch(localVideoZoomChanged(_stageVideo.videoType, zoomState));
            }
        } else if (_isModerator) {
            this._sendModeratorZoom(zoomState);
            dispatch(remoteVideoZoomChanged(_stageVideo.participantId, _stageVideo.videoType, zoomState));
        }
    };

    /**
     * Callback when user approves sharing the zoom with all participants.
     *
     * @param {boolean} isModeratorPinnedRemote - Whether this is for moderator-pinned remote zoom.
     * @private
     * @returns {void}
     */
    _onConfirmShareZoom = isModeratorPinnedRemote => {
        if (isModeratorPinnedRemote) {
            this._moderatorZoomShareApproved = true;
            this.props.dispatch(setModeratorZoomShareApproved(true));
        } else {
            this._localZoomShareApproved = true;
            this.props.dispatch(setLocalZoomShareApproved(true));
        }

        this._isZoomConfirmationDialogOpen = false;

        const currentTransform = this.prismaZoomRef.current?.getTransform() || this._pendingZoomTransform;

        this._pendingZoomTransform = null;

        if (currentTransform) {
            this._broadcastZoom(currentTransform);
        }
    };

    /**
     * Callback when user declines sharing the zoom with all participants.
     *
     * @param {boolean} isModeratorPinnedRemote - Whether this is for moderator-pinned remote zoom.
     * @private
     * @returns {void}
     */
    _onDeclineShareZoom = isModeratorPinnedRemote => {
        if (isModeratorPinnedRemote) {
            this._moderatorZoomShareApproved = false;
            this.props.dispatch(setModeratorZoomShareApproved(false));
        } else {
            this._localZoomShareApproved = false;
            this.props.dispatch(setLocalZoomShareApproved(false));
        }

        this._isZoomConfirmationDialogOpen = false;
        this._pendingZoomTransform = null;
    };

    /**
     * Sends the zoom of a moderator-pinned participant to all participants via data channel.
     *
     * @param {Object} zoomState - The normalized zoom state.
     * @param {Object} [targetStageVideo] - The stage video to use.
     * @private
     * @returns {void}
     */
    _sendModeratorZoom = (zoomState, targetStageVideo) => {
        const stageVideo = targetStageVideo || this.props._stageVideo;
        const { _conference } = this.props;

        if (!_conference || !stageVideo) {
            return;
        }

        const isIdentity = isSameZoomState(zoomState, IDENTITY_ZOOM_STATE);

        if (isIdentity) {
            if (this._moderatorZoomTimer) {
                clearTimeout(this._moderatorZoomTimer);
                this._moderatorZoomTimer = null;
            }
            this._pendingModeratorZoomState = null;
            this._doSendModeratorZoom(IDENTITY_ZOOM_STATE, stageVideo);

            return;
        }

        this._pendingModeratorZoomState = zoomState;

        if (this._moderatorZoomTimer) {
            return;
        }

        this._doSendModeratorZoom(zoomState, stageVideo);

        this._moderatorZoomTimer = setTimeout(() => {
            this._moderatorZoomTimer = null;

            if (this._pendingModeratorZoomState
                    && !isSameZoomState(this._lastSentModeratorZoom, this._pendingModeratorZoomState)) {
                const pending = this._pendingModeratorZoomState;

                this._pendingModeratorZoomState = null;
                this._sendModeratorZoom(pending, stageVideo);
            }
        }, ZOOM_SYNC_SEND_INTERVAL_MS);
    };

    /**
     * Sends the moderator zoom message over conference data channel immediately.
     *
     * @param {Object} zoomState - The normalized zoom state.
     * @param {Object} stageVideo - The target stage video.
     * @private
     * @returns {void}
     */
    _doSendModeratorZoom = (zoomState, stageVideo) => {
        const { _conference } = this.props;

        if (!_conference || !stageVideo) {
            return;
        }

        try {
            const myId = typeof _conference.myUserId === 'function' ? _conference.myUserId() : undefined;

            _conference.sendMessage({
                type: 'MODERATOR_ZOOM_SYNC',
                participantId: stageVideo.participantId,
                videoType: stageVideo.videoType,
                zoomState: {
                    zoom: zoomState.zoom,
                    cx: zoomState.cx,
                    cy: zoomState.cy,
                    ar: zoomState.ar
                },
                senderId: myId
            });
            this._lastSentModeratorZoom = zoomState;
        } catch (error) {
            logger.warn('Failed to send moderator zoom sync', error);
        }
    };

    /**
     * Re-applies or re-shares the zoom after the large video box changed
     * size; the normalized state stays, the pixel transform does not.
     *
     * @returns {void}
     */
    _onStageResize = () => {
        if (this._stageResizeFrame) {
            return;
        }

        this._stageResizeFrame = requestAnimationFrame(() => {
            this._stageResizeFrame = null;

            if (this._appliedStageZoomKey) {
                this._applyStageZoom();
            } else if (this._isViewZoomShared() && this.prismaZoomRef.current) {
                this._onTransformChange(this.prismaZoomRef.current.getTransform());
            }
        });
    };

    /**
     * Reacts to stage / zoom prop changes: applies a remote participant's
     * zoom to their video, restores the local user's own shared zoom when
     * their video returns to stage, and serves the level button.
     *
     * @param {Object} prevProps - The previous props.
     * @returns {void}
     */
    _updateStageZoom = prevProps => {
        const {
            _localFlipX,
            _mhConferenceJoined,
            _remoteVideoZoom,
            _requestedViewZoom,
            _stageVideo,
            _isStageParticipantModeratorPinned
        } = this.props;
        const stageChanged = this._stageKey(_stageVideo) !== this._stageKey(prevProps._stageVideo);

        if (stageChanged) {
            // No previous stage video (e.g. the pinned participant's camera
            // was off): nothing was zoomed, so there is nothing to reset.
            const prevWasLocal = Boolean(prevProps._stageVideo?.local);
            const prevWasModeratorPinned = Boolean(prevProps._stageVideo
                && prevProps._isModerator
                && prevProps._isStageParticipantModeratorPinned
                && !prevProps._stageVideo.local);
            const prevApproved = prevWasLocal
                ? this._getZoomShareApproved(false)
                : prevWasModeratorPinned ? this._getZoomShareApproved(true) : false;

            if (prevApproved) {
                if (prevWasLocal) {
                    this.props.dispatch(localVideoZoomChanged(prevProps._stageVideo.videoType, IDENTITY_ZOOM_STATE));
                } else if (prevWasModeratorPinned) {
                    this._sendModeratorZoom(IDENTITY_ZOOM_STATE, prevProps._stageVideo);
                    this.props.dispatch(remoteVideoZoomChanged(prevProps._stageVideo.participantId, prevProps._stageVideo.videoType, IDENTITY_ZOOM_STATE));
                }
            }

            if (this._isZoomConfirmationDialogOpen) {
                this._isZoomConfirmationDialogOpen = false;
                this.props.dispatch(hideDialog(ZoomShareDialog));
            }

            this._pendingZoomTransform = null;
            this._isActivelyControllingZoom = false;

            this._applyStageZoom();
        } else {
            if (prevProps._isStageParticipantModeratorPinned && !_isStageParticipantModeratorPinned) {
                if (_stageVideo) {
                    this.props.dispatch(remoteVideoZoomChanged(_stageVideo.participantId, _stageVideo.videoType, IDENTITY_ZOOM_STATE));
                }
                this._isActivelyControllingZoom = false;
            }

            if (_remoteVideoZoom !== prevProps._remoteVideoZoom
                    || (_mhConferenceJoined && !prevProps._mhConferenceJoined)
                    || (_isStageParticipantModeratorPinned !== prevProps._isStageParticipantModeratorPinned)) {
                if (!this._isActivelyControllingZoom) {
                    this._applyStageZoom();
                }
            }
        }

        // Mirroring toggled under an unchanged transform: the shared center
        // point is now on the other side. Wait a frame for the video
        // element's style to follow the setting before re-reading it.
        if (_localFlipX !== prevProps._localFlipX) {
            VideoLayout.setLocalFlipX(_localFlipX);
            if (this.mainVideoRef.current && (this.props.isLocal || this.props._participants?.find(p => p?.id === this.props._participantId)?.local) && !this.props.isLocalParticipantScreenSharing) {
                this.mainVideoRef.current.style.transform = _localFlipX ? 'scaleX(-1)' : 'none';
            }
            if (this._isViewZoomShared()) {
                requestAnimationFrame(() => {
                    this.prismaZoomRef.current
                        && this._onTransformChange(this.prismaZoomRef.current.getTransform());
                });
            }
        }

        if (_requestedViewZoom
                && _requestedViewZoom !== prevProps._requestedViewZoom
                && this._isViewZoomShared()
                && this.prismaZoomRef.current) {
            this.prismaZoomRef.current.setZoomLevel(_requestedViewZoom.zoom);
        }
    };

    /**
     * Puts the large video at the zoom that belongs to the video on stage: the
     * owner's shared framing for a remote video, the locally shared framing
     * for the local user's own video, or 1x once such a framing no longer
     * applies. The viewer may still zoom on top; the next shared update wins.
     *
     * @returns {void}
     */
    _applyStageZoom = () => {
        const prisma = this.prismaZoomRef.current;
        const { _localVideoZoom, _remoteVideoZoom, _stageObjectFit, _stageVideo, _isStageParticipantModeratorPinned } = this.props;

        if (!prisma) {
            return;
        }

        if (this._isActivelyControllingZoom) {
            return;
        }

        const isModeratorApproved = this.props._moderatorZoomShareApproved !== false;
        let shared = isModeratorApproved ? _remoteVideoZoom : undefined;

        if (_stageVideo?.local) {
            const moderatorZoom = _isStageParticipantModeratorPinned ? _remoteVideoZoom : undefined;
            const isLocalApproved = this._getZoomShareApproved(false);

            shared = moderatorZoom || (this._isViewZoomShared() && isLocalApproved ? _localVideoZoom : undefined);
        }
        const key = this._stageKey(_stageVideo);

        if (shared && shared.zoom > 1 + ZOOM_SYNC_EPSILON) {
            // Shared states describe the unmirrored picture; a mirrored
            // preview (own self view) shows it flipped.
            const displayed = this._isStageVideoMirrored() ? flipZoomStateX(shared) : shared;

            // Animate towards a remote owner's framing (their updates are
            // throttled); restore the local user's own framing instantly unless it was a moderator zoom.
            const animate = !_stageVideo?.local || Boolean(_isStageParticipantModeratorPinned && _remoteVideoZoom);

            this._isApplyingStageZoom = true;
            prisma.setTransform(
                toTransform(displayed, this._getStageBox(), _stageObjectFit), animate);
            this._appliedStageZoomKey = key;
        } else if (this._appliedStageZoomKey) {
            this._isApplyingStageZoom = true;
            prisma.reset();
            this._appliedStageZoomKey = null;
        }
    };

    /**
     * Two-finger pinch on the local camera / screenshare: changes the capture
     * zoom that remote participants receive, instead of scaling the preview.
     * Spreading past 10x does nothing; pinching in walks back to 1x. The
     * running value is kept here because redux props can lag a touch move.
     *
     * @param {number} ratio - Finger distance relative to the previous move.
     * @returns {void}
     */
    _onCapturePinch = ratio => {
        const { _cameraZoom, _captureZoomType, _screenshareZoom, dispatch } = this.props;

        if (!_captureZoomType || !Number.isFinite(ratio) || ratio <= 0) {
            return;
        }

        const base = this._pinchZoom
            ?? (_captureZoomType === VIDEO_TYPE.DESKTOP ? _screenshareZoom : _cameraZoom)
            ?? CAMERA_ZOOM_MIN;
        const next = Math.round(
            Math.min(CAMERA_ZOOM_MAX, Math.max(CAMERA_ZOOM_MIN, base * ratio)) * 100
        ) / 100;

        if (next === this._pinchZoom) {
            return;
        }

        this._pinchZoom = next;
        dispatch(_captureZoomType === VIDEO_TYPE.DESKTOP ? setScreenshareZoom(next) : setCameraZoom(next));
    };

    /**
     * Ends a capture-zoom pinch; the next one starts from the stored value.
     *
     * @returns {void}
     */
    _onCapturePinchEnd = () => {
        this._pinchZoom = null;
    };

    /**
   * Handles the context menu event on the large video container.
   *
   * @param {MouseEvent} event - The context menu event.
   * @private
   * @returns {void}
   */
    _handleContextMenu(event) {
        const { _participantId, _participants, isLocal: isLocalProp } = this.props;
        const participant = _participants?.find(p => p?.id === _participantId)
            || (isLocalProp ? _participants?.find(p => p?.local) : undefined);

        // Check if the participant is local
        const isLocal = Boolean(isLocalProp || participant?.local);

        if (isLocal) {
            showLocalVideoContextMenu(event, 'largeVideoContainer');
        } else {
            showRemoteVideoContextMenu(event, 'largeVideoContainer');
        }
    }

    /**
   * Resumes the local participant's self-view video inside the
   * Picture-in-Picture window.
   *
   * When Picture-in-Picture is entered automatically (the browser's auto-PiP as
   * the tab is being hidden), the local {@code <video>} element's rendering
   * pipeline is suspended while the source document is hidden. Moving it into
   * the visible PiP window does not always resume painting, so the local tile
   * appears blank/frozen and stays frozen after returning. Re-binding the same
   * {@code srcObject} and calling {@code play()} forces the browser to
   * re-establish the pipeline. The re-bind is only performed when the source
   * document was hidden (auto-PiP) to avoid an unnecessary flicker on manual
   * PiP, which already renders correctly.
   *
   * @param {Document} pipDocument - The Picture-in-Picture window document.
   * @param {boolean} forceRebind - Whether to re-bind the srcObject to force a
   * pipeline reset (needed for auto-PiP from a hidden tab).
   * @private
   * @returns {void}
   */
    _resumeLocalPipVideo(pipDocument, forceRebind = false) {
        const doc = pipDocument || document;
        const video = doc.getElementById('localVideo_container');

        if (!video) {
            return;
        }

        try {
            if (forceRebind && video.srcObject) {
                const { srcObject } = video;

                video.srcObject = null;
                video.srcObject = srcObject;
            }

            const playPromise = video.play();

            if (playPromise && typeof playPromise.catch === 'function') {
                // Ignore expected autoplay/reflow interruption errors.
                playPromise.catch(() => { /* no-op */ });
            }
        } catch (error) {
            logger.warn('Failed to resume local video in Picture-in-Picture window:', error);
        }
    }

    /**
   * Enters Picture-in-Picture mode for the video element.
   *
   * @private
   * @returns {void}
   */
    async _enterPictureInPicture() {
        // Claim this enter attempt. Any exit, or a later enter, supersedes it.
        const pipSessionId = ++this._pipSessionId;

        // Capture whether the source document is hidden at trigger time. This is
        // true for the browser's auto-PiP (tab switched away), where the local
        // video pipeline gets suspended and must be re-bound to resume in PiP.
        const sourceWasHidden
            = typeof document !== 'undefined'
            && (document.hidden || document.visibilityState === 'hidden');

        // Only prevent entering PiP if local user is sharing YouTube
        if (this.props._isLocalUserSharingYouTube) {
            logger.log(
        'Cannot enter Picture-in-Picture mode while local user is sharing YouTube'
            );
            this.props._setPictureInPictureModeConfig(false);

            return;
        }

        if (this.mainVideoRef.current && document.pictureInPictureEnabled) {
            if (_canUseDocumentPictureInPicture()) {
                // Document PiP is only allowed while a user gesture is still 'transiently active'. This method
                // runs from componentDidUpdate, so it also fires for state changes that no click produced (a
                // restored preference, an automatic trigger), and requestWindow() then throws NotAllowedError.
                // Checking first turns an exception into a clean no-op.
                if (navigator.userActivation && !navigator.userActivation.isActive) {
                    logger.warn('Not entering Picture-in-Picture mode: no user activation');

                    this.props._setPictureInPictureModeConfig(false);

                    return;
                }

                try {
                    // PiP can only be turned on after it was turned off, and turning it off returns the root, so
                    // a root still out of the main document here is one an earlier exit failed to bring back.
                    // Bring it back now: otherwise the lookup below finds nothing, the new window opens empty and
                    // the conference is covered by the Exit overlay with nothing behind it.
                    if (!document.querySelector('#react')) {
                        restorePipAdoption();
                    }

                    const player = document.querySelector('#react');

                    if (!player) {
                        logger.error('Not entering Picture-in-Picture mode: the application root is missing');

                        this.props._setPictureInPictureModeConfig(false);

                        return;
                    }

                    const playerContainer = document.querySelector(
            '#react-picture-in-picture'
                    ) || player.parentElement || document.body;

                    // Open a Picture-in-Picture window
                    const pipWindow = await documentPictureInPicture.requestWindow({
                        width: '350',
                        height: '600',
                        preferInitialWindowPlacement: true
                    });

                    // PiP was turned off again while requestWindow() was in flight, so this window is
                    // already unwanted. Close it rather than leaving an orphan the exit path can no
                    // longer reach, and leave this.pipWindow alone - the exit has already cleared it.
                    if (pipSessionId !== this._pipSessionId) {
                        pipWindow.close();

                        return;
                    }

                    this.pipWindow = pipWindow;

                    // PiP can be closed during the stylesheet/font await below, which takes seconds in the
                    // worst case. If that happens, the window is already closed and we have no PiP to render into
                    // anyway. Return now rather than let the copy fail or leave a half-setup window.
                    if (pipWindow.closed) {
                        this.pipWindow = null;
                        pipWindow.close();

                        // Nothing else will turn PiP off for this attempt - no window, no pagehide - so without
                        // this the state stays on, the next automatic trigger sets it to on again, that is no
                        // change, and PiP never starts again for the rest of the call.
                        this.props._setPictureInPictureModeConfig(false);

                        return;
                    }

                    // Copy all styles using the robust utility function
                    await copyStylesToPipWindow(pipWindow, {
                        copyFonts: true,
                        copyAnimations: true,
                        addFallbackStyles: true,
                        onError: error => {
                            const message = `${error?.message || ''}`;

                            if (/Timeout loading/i.test(message)
                                && /(fonts\.googleapis\.com|fonts\.gstatic\.com)/i.test(message)) {
                                logger.warn('Optional PiP font stylesheet timeout:', error);

                                return;
                            }

                            logger.error('Error copying styles to PiP:', error);
                        }
                    });

                    // Copying styles awaits stylesheet and font loading, which can take seconds when a
                    // remote font stylesheet is slow. If the user closed PiP during that window,
                    // _exitPictureInPicture has already closed this window and cleared this.pipWindow -
                    // everything below would throw on a null dereference, and the style watcher installed
                    // just after would never be cleaned up. Stop here instead.
                    if (pipSessionId !== this._pipSessionId) {
                        return;
                    }

                    // Closed on its own rather than superseded: Chrome closes the automatic PiP window as soon as
                    // the tab is shown again, and on a slow machine that happens while the styles are still
                    // copying. Nothing else will turn the state off, so do it here, or the next automatic
                    // trigger sets it to on again, that is no change, and PiP never starts again.
                    if (pipWindow.closed) {
                        this.pipWindow = null;
                        this.props._setPictureInPictureModeConfig(false);

                        return;
                    }

                    // Watch for dynamic style changes (e.g., styled-components updates)
                    this.styleWatcherCleanup = watchStyleChanges(pipWindow, {
                        debounceMs: 100
                    });

                    // Add viewport meta tag
                    const metaDescription = document.createElement('meta');

                    metaDescription.setAttribute('name', 'viewport');
                    metaDescription.setAttribute(
            'content',
            'width=device-width, initial-scale=1.0,user-scalable=0'
                    );
                    pipWindow.document.head.appendChild(metaDescription);

                    // Move the player to the Picture-in-Picture window.
                    //
                    // Appending a node from another document adopts it, and the browser reloads every iframe in the
                    // adopted subtree and pauses its media elements. `player` here is the whole application root, so
                    // that hits everything the conference is showing - a shared video came back reloaded, rewound and
                    // playing even when the user had paused it. Carry that state across the move.
                    appendPreservingState(pipWindow.document.body, player);
                    pipAdoption = {
                        root: player,
                        home: playerContainer
                    };

                    // Re-run layout after DOM adoption into PiP so the local
                    // thumbnail container is relocated into the active PiP
                    // document for both horizontal and vertical filmstrip views.
                    const localPipVideo = pipWindow.document.getElementById('localVideo_container');
                    const shouldForceLayoutSync = !localPipVideo;
                    const syncPipLayout = () => {
                        VideoLayout.updateAllVideos();
                        VideoLayout.refreshLayout();
                    };

                    if (shouldForceLayoutSync) {
                        window.requestAnimationFrame(syncPipLayout);
                    }

                    // Auto-PiP (tab hidden at trigger) suspends the local
                    // self-view pipeline; re-bind + resume so the local tile is
                    // visible in PiP and does not return frozen. Manual PiP only
                    // gets a harmless play() (no re-bind, no flicker).
                    this._resumeLocalPipVideo(pipWindow.document, sourceWasHidden);

                    // A delayed second pass is only needed when the local tile
                    // container race prevented immediate attachment in PiP.
                    if (shouldForceLayoutSync) {
                        setTimeout(
                            () => {
                                syncPipLayout();
                                this._resumeLocalPipVideo(this.pipWindow?.document, sourceWasHidden);
                            },
                            300
                        );
                    }

                    // Auto-PiP can suspend local video pipeline without a DOM
                    // attach race; do one delayed resume-only pass to avoid
                    // extra relayout flicker in manual PiP.
                    if (sourceWasHidden && !shouldForceLayoutSync) {
                        setTimeout(
                            () => this._resumeLocalPipVideo(this.pipWindow?.document, true),
                            220
                        );
                    }

                    const pipOverlayEle = document.createElement('div');

                    pipOverlayEle.id = 'pip-overlay';

                    pipOverlayEle.style.background = '#000000cf';
                    pipOverlayEle.style.color = '#fff';

                    pipOverlayEle.style.padding = '10px';
                    pipOverlayEle.style.textAlign = 'center';
                    pipOverlayEle.style.animation = 'fadeInOut 2s ease-in-out';
                    pipOverlayEle.style.position = 'absolute';
                    pipOverlayEle.style.zIndex = '10000';
                    pipOverlayEle.style.display = 'flex';
                    pipOverlayEle.style.justifyContent = 'center';
                    pipOverlayEle.style.width = '100%';
                    pipOverlayEle.style.height = '100%';
                    pipOverlayEle.style.alignItems = 'center';
                    pipOverlayEle.style.fontSize = '23px';
                    pipOverlayEle.style.marginTop = '0px';
                    pipOverlayEle.style.borderRadius = '0px';
                    pipOverlayEle.style.flexDirection = 'column';
                    pipOverlayEle.style.gap = '10px';
                    pipOverlayEle.innerHTML = '<div><svg xmlns="http://www.w3.org/2000/svg" '
                        + 'width="26" height="26" viewBox="0 0 24 24"><g fill="none" stroke="white" '
                        + 'stroke-linecap="round" stroke-linejoin="round" stroke-width="2">'
                        + '<path d="M8 4.5v5H3m-1-6l6 6m13 0v-3c0-1.16-.84-2-2-2h-7m-9 9v2c0 1.05.95 2 2 2h3"/>'
                        + '<rect width="10" height="7" x="12" y="13.5" ry="2"/></g></svg>'
                        + ` &nbsp; ${this.props.t('toolbar.accessibilityLabel.pipMode')} </div>`;

                    const exitPipBtn = document.createElement('button');

                    exitPipBtn.id = 'exit-pip-btn';
                    exitPipBtn.textContent = this.props.t('toolbar.accessibilityLabel.exitPip') || 'Exit Picture-in-Picture';
                    exitPipBtn.style.padding = '8px 16px';
                    exitPipBtn.style.fontSize = '14px';
                    exitPipBtn.style.fontWeight = '500';
                    exitPipBtn.style.color = '#fff';
                    exitPipBtn.style.backgroundColor = 'rgb(75, 103, 144)';
                    exitPipBtn.style.border = '1px solid rgb(75, 103, 144)';
                    exitPipBtn.style.borderRadius = '6px';
                    exitPipBtn.style.cursor = 'pointer';
                    exitPipBtn.style.marginTop = '10px';
                    exitPipBtn.style.transition = 'background-color 0.2s ease';

                    exitPipBtn.onmouseover = () => {
                        exitPipBtn.style.backgroundColor = 'rgba(75, 103, 144, 0.25)';
                    };
                    exitPipBtn.onmouseout = () => {
                        exitPipBtn.style.backgroundColor = 'rgba(75, 103, 144, 0.15)';
                    };

                    exitPipBtn.addEventListener('click', () => {
                        try {
                            if (typeof this._exitPictureInPicture === 'function') {
                                this._exitPictureInPicture();
                            }
                            if (this.props && typeof this.props._setPictureInPictureModeConfig === 'function') {
                                this.props._setPictureInPictureModeConfig(false);
                            }
                        } catch (err) {
                            logger.error('Error exiting Picture-in-Picture mode:', err);
                        }
                    });

                    pipOverlayEle.appendChild(exitPipBtn);

                    playerContainer.appendChild(pipOverlayEle);

                    // Set up MutationObserver to auto-play remote participant audio elements
                    // added to the PiP window after it opens (e.g., when a new participant
                    // joins). The browser's autoplay policy can block audio for newly-added
                    // <audio> elements in the PiP window context, so we call play() on
                    // remote audio elements only (id starts with 'remoteAudio_').
                    this._setupPipAudioObserver(pipWindow);

                    pipWindow.addEventListener('pagehide', event => {
                        // Closing PiP adopts the application root a second time, back into the main document,
                        // with the same cost - restorePipAdoption gives it the same protection as the move in.
                        // Scoped to this window's document so a superseded window cannot take the root back
                        // from a newer one.
                        restorePipAdoption(event.target);

                        // Clean up audio observer when PiP closes
                        this._cleanupPipAudioObserver();

                        // Clean up style watcher when PiP closes
                        if (this.styleWatcherCleanup) {
                            this.styleWatcherCleanup();
                            this.styleWatcherCleanup = null;
                        }

                        this.props._setPictureInPictureModeConfig(false);

                        setTimeout(() => {
                            this.forceUpdate();
                            VideoLayout.refreshLayout();
                        }, 100);
                    });
                } catch (error) {
                    logger.error(
            'Failed to enter latest Picture-in-Picture mode:',
            error
                    );

                    // Revert the state if PiP failed
                    this.props._setPictureInPictureModeConfig(false);
                }
            } else {
                logger.log('Document picture in picture support not found');

                this._requestVideoPictureInPicture(this.mainVideoRef.current);
            }
        } else {
            logger.error(
        'Picture-in-Picture is not supported or video element not available'
            );

            // Revert the state if PiP is not supported
            this.props._setPictureInPictureModeConfig(false);
        }
    }

    /**
     * Puts the given video element into the classic (element) Picture-in-Picture mode.
     *
     * @param {HTMLVideoElement} video - The large video element.
     * @param {boolean} [isRetry] - Whether this is the retry that follows 'loadedmetadata'.
     * @private
     * @returns {void}
     */
    _requestVideoPictureInPicture(video, isRetry = false) {
        // requestPictureInPicture() rejects with InvalidStateError until the element has metadata. That is the
        // normal state right after the large video is switched to another participant, so rather than reporting a
        // failure, wait for the metadata once and try again.
        if (video.readyState < HTMLMediaElement.HAVE_METADATA) {
            if (isRetry) {
                logger.warn('Not entering Picture-in-Picture mode: the video element has no metadata');

                this.props._setPictureInPictureModeConfig(false);

                return;
            }

            let metadataTimeout = null;
            const onLoadedMetadata = () => {
                clearTimeout(metadataTimeout);
                this._requestVideoPictureInPicture(video, true);
            };

            // The wait is bounded: if no frames ever arrive the PiP state has to be reverted, otherwise the
            // toolbar keeps showing Picture-in-Picture as active while no window was ever opened.
            metadataTimeout = setTimeout(() => {
                video.removeEventListener('loadedmetadata', onLoadedMetadata);
                logger.warn('Not entering Picture-in-Picture mode: timed out waiting for video metadata');

                this.props._setPictureInPictureModeConfig(false);
            }, PIP_METADATA_TIMEOUT_MS);

            video.addEventListener('loadedmetadata', onLoadedMetadata, { once: true });

            return;
        }

        video.requestPictureInPicture()
            .then(() => {
                logger.log('Successfully entered Picture-in-Picture mode');
            })
            .catch(error => {
                logger.warn(`Failed to enter Picture-in-Picture mode: ${error?.name}: ${error?.message}`);

                // Revert the state if PiP failed
                this.props._setPictureInPictureModeConfig(false);
            });
    }

    /**
   * Exits Picture-in-Picture mode.
   *
   * @private
   * @returns {void}
   */
    // eslint-disable-next-line require-await -- async by contract: callers rely on the returned promise
    async _exitPictureInPicture() {
        // Supersede any enter attempt still awaiting requestWindow() or the stylesheet copy.
        this._pipSessionId++;

        if (_canUseDocumentPictureInPicture()) {
            try {

                // Clean up audio observer
                this._cleanupPipAudioObserver();

                // Clean up style watcher
                if (this.styleWatcherCleanup) {
                    this.styleWatcherCleanup();
                    this.styleWatcherCleanup = null;
                }

                // Fall back to the browser's own record of the PiP window: this instance may not be the one
                // that opened it (LargeVideo can be remounted while PiP is up), and then this.pipWindow is null
                // while the window, and the conference inside it, are still there.
                const pipWindow = this.pipWindow || window.documentPictureInPicture?.window;

                if (pipWindow && !pipWindow.closed) {
                    pipWindow.close();
                }
                this.pipWindow = null;
            } catch (error) {
                logger.log('Failed to exit latest Picture-in-Picture mode:', error);
            }

            // Do not leave returning the conference to the window's pagehide: when the window is already gone
            // there is no pagehide to come, and the user is left on the Exit overlay for good.
            restorePipAdoption();
        } else if (document.pictureInPictureElement) {
            document.exitPictureInPicture()
                .then(() => {
                    logger.log('Successfully exited Picture-in-Picture mode');
                })
                .catch(error => {
                    logger.warn('Failed to exit Picture-in-Picture mode:', error);
                });
        }

        this.props._setPictureInPictureModeConfig(false);

        setTimeout(() => {
            this.forceUpdate();
            VideoLayout.refreshLayout();

            // if (window.APP && window.APP.UI) {
            //     const { _participantId } = this.props;

            //     window.APP.UI.updateLargeVideo(_participantId, true);
            // }
        }, 100);
    }

    /**
     * Returns the conference to the tab when the user comes back to it and the PiP window is gone.
     *
     * Automatic PiP is tied to the tab being hidden: Chrome closes that window as soon as the tab is shown again.
     * Switching tabs back and forth faster than a slow machine can set PiP up leaves attempts that never finish -
     * a window closed while the styles were still copying, or the conference moved into a window that then went
     * away without its pagehide bringing it back - and the user returns to the Exit overlay, or to a PiP state
     * that is on with no window, which the next automatic trigger cannot switch on again. A manual PiP window
     * stays open while the tab is visible, so it is left alone; this only acts once there is no window.
     *
     * @private
     * @returns {void}
     */
    _onVisibilityChangeForPip() {
        if (document.visibilityState !== 'visible' || !('documentPictureInPicture' in window)) {
            return;
        }

        const pipWindow = window.documentPictureInPicture.window;

        if (pipWindow && !pipWindow.closed) {
            return;
        }

        const stranded = Boolean(pipAdoption) || Boolean(document.getElementById('pip-overlay'));

        if (stranded) {
            logger.warn('Picture-in-Picture window is gone but the conference was not returned to the tab;'
                + ' returning it now');
            restorePipAdoption();
        }

        this.pipWindow = null;

        if (stranded || this.props._isPictureInPictureVisible) {
            this.props._setPictureInPictureModeConfig(false);
        }
    }

    /**
     * Sets up a MutationObserver on the PiP window to detect and auto-play
     * new remote participant <audio> elements added after the PiP window is
     * opened.  Only elements whose id starts with 'remoteAudio_' are played;
     * all other audio elements (notification sounds, UI effects, etc.) are
     * left untouched.
     *
     * @param {Window} pipWin - The PiP window object.
     * @private
     * @returns {void}
     */
    _setupPipAudioObserver(pipWin) {
        if (!pipWin || !pipWin.document) {
            return;
        }

        // Disconnect any existing observer before creating a new one
        this._cleanupPipAudioObserver();

        this._pipAudioObserver = new MutationObserver(mutations => {
            for (const mutation of mutations) {
                for (const node of mutation.addedNodes) {
                    if (node.nodeType === Node.ELEMENT_NODE) {
                        // Check if the added node itself is a remote participant audio element
                        if (node.tagName === 'AUDIO' && this._isRemoteParticipantAudio(node)) {
                            this._playAudioElement(node);
                        }

                        // Also check descendants of the added node for remote audio elements
                        // (e.g., when a Thumbnail subtree is inserted)
                        const audioDescendants = node.querySelectorAll('audio[id^="remoteAudio_"]');

                        audioDescendants.forEach(audioEl => {
                            this._playAudioElement(audioEl);
                        });
                    }
                }
            }
        });

        // Observe the entire PiP document body for any subtree additions
        this._pipAudioObserver.observe(pipWin.document.body, {
            childList: true,
            subtree: true
        });

        logger.log('PiP audio MutationObserver started — watching for new remote participant audio');
    }

    /**
     * Returns true if the given audio element is a remote participant audio
     * track (as opposed to a notification sound, UI effect, etc.).
     *
     * Remote participant audio elements are rendered by the Thumbnail component
     * with ids of the form 'remoteAudio_<trackId>'.
     *
     * @param {HTMLAudioElement} audioEl - The audio element to test.
     * @private
     * @returns {boolean}
     */
    _isRemoteParticipantAudio(audioEl) {
        return audioEl && audioEl.id && audioEl.id.startsWith('remoteAudio_');
    }

    /**
     * Returns true if the given audio element is still in a state where play() can succeed, i.e. it is paused,
     * unmuted and still attached to a document.
     *
     * @param {HTMLAudioElement} audioEl - The audio element to test.
     * @private
     * @returns {boolean}
     */
    _isPlayableAudioElement(audioEl) {
        return Boolean(audioEl)
            && audioEl.paused !== false
            && !audioEl.muted
            && audioEl.isConnected !== false;
    }

    /**
     * Attempts to play a remote participant audio element, handling autoplay-policy
     * errors. Skips elements that are already playing or are muted.
     *
     * @param {HTMLAudioElement} audioEl - The audio element to play.
     * @private
     * @returns {void}
     */
    _playAudioElement(audioEl) {
        // Skip elements that cannot or need not be played: already playing, muted, or no longer attached to a
        // document/stream. The last case is the common one here - a thumbnail that React re-rendered or removed
        // between the mutation record and this call. Calling play() on it can only reject.
        if (!this._isPlayableAudioElement(audioEl)) {
            return;
        }

        const playPromise = audioEl.play();

        if (playPromise !== undefined) {
            playPromise.catch(error => {
                // NotAllowedError means autoplay was blocked; retry once after a
                // short delay to give the browser time to settle.
                if (error.name === 'NotAllowedError') {
                    logger.warn(
                        `PiP audio autoplay blocked for element [id=${audioEl.id}], retrying…`
                    );
                    setTimeout(() => {
                        // Re-check that the element is still worth playing before retrying.
                        if (!this._isPlayableAudioElement(audioEl)) {
                            return;
                        }
                        audioEl.play().catch(retryError => {
                            // DOMExceptions serialize to '{}', which is what this log used to print. Log the
                            // name and message so an autoplay-policy block can be told apart from a teardown
                            // race (AbortError), which is expected and not worth a warning.
                            if (retryError?.name === 'AbortError') {
                                logger.debug(
                                    `PiP audio play aborted for [id=${audioEl.id}]: ${retryError.message}`
                                );

                                return;
                            }

                            logger.warn(
                                `PiP audio play retry failed for [id=${audioEl.id}]: `
                                    + `${retryError?.name}: ${retryError?.message}`
                            );
                        });
                    }, 500);
                } else if (error?.name === 'AbortError') {
                    logger.debug(`PiP audio play aborted for [id=${audioEl.id}]: ${error.message}`);
                } else {
                    logger.warn(`PiP audio play failed for [id=${audioEl.id}]: ${error?.name}: ${error?.message}`);
                }
            });
        }
    }

    /**
     * Disconnects the PiP audio MutationObserver if it is active.
     *
     * @private
     * @returns {void}
     */
    _cleanupPipAudioObserver() {
        if (this._pipAudioObserver) {
            this._pipAudioObserver.disconnect();
            this._pipAudioObserver = null;
            logger.log('PiP audio MutationObserver stopped');
        }
    }

    /**
   * Implements React's {@link Component#render()}.
   *
   * @inheritdoc
   * @returns {React$Element}
   */
    render() {
        const {
            _localFlipX,
            _participantId,
            _participants,
            _showVideoMutedIndicator,
            _filmStripView,
            _showAudioMutedIndicator,
            _conferenceView,
            _iAmRecorder,
            objectFit,
            _participantName,
            _designation,
            _organization,
            isLocalParticipantScreenSharing,
            _isModerator,
            _isVisible,
            _isCustomParticipantVisible,
            screenSharingParticipant,
            _isCustomParticipantEveryone,
            _shouldDisplayTileView,
            _isPictureInPictureVisible,
            t
        } = this.props;
        const style = this._getCustomSyles();
        const className
      = _filmStripView === LAYOUTS.TILE_VIEW && !_shouldDisplayTileView
          ? 'videocontainer videocontainer-tileview'
          : isLocalParticipantScreenSharing ? 'videocontainer local-screen-share' : 'videocontainer';
        const audioLevel = this.props._showAudioMutedIndicator || this.props._audioTrack?.muted ? 0 : this.state.audioLevel;
        let participant = _participants?.find(p => p?.id === _participantId)
            || (this.props.isLocal ? _participants?.find(p => p?.local) : null);

        // Visitors never have a self tile. If large-video still points at local
        // (stale id from before the vnode redirect), prefer a remote so stage
        // and vertical actually show the host track.
        if (this.props._iAmVisitor && participant?.local && !participant?.pinned && _participants?.some(p => !p?.local && !p?.botType)) {
            const pinned = _participants.find(p => p?.pinned && !p?.local);
            const dominant = _participants.find(p => p?.dominantSpeaker && !p?.local);
            const lastRemote = _participants.filter(p => !p?.local && !p?.botType).pop();

            participant = pinned || dominant || lastRemote || participant;
        }

        // No participant to show: nothing selected yet, or the list is empty while switching rooms. This used to return
        // a separate "Loading..." tree, which unmounted the whole stage - the next render mounted a new <video>, zoom box
        // and background container, while VideoContainer, the stage ResizeObserver and the listeners added in
        // componentDidMount stayed bound to the old nodes. The stage now stays mounted with "Loading..." drawn over it,
        // and the re-election that was dispatched from here runs after the render (_reelectMissingParticipant).
        const showLoading = !participant && _filmStripView !== LAYOUTS.TILE_VIEW;

        // Keep #largeVideo in the DOM. Replacing the whole container with a
        // Loading stub unmounts the <video> VideoContainer attaches to, so a
        // visitor landing in stage/vertical never sees the remote track.

        const localDisplayName = participant?.name || this.props._localParticipant?.name || _participantName;
        const participantDisplayName = participant?.local
            ? localDisplayName ? `${localDisplayName} (${t('me')})` : t('me')
            : participant?.name || _participantName;
        const raisedHand = participant?.raisedHand;
        const dominantSpeakerStyle = {
            display: _participants.length === 1 && _iAmRecorder ? 'block' : ''
        };

        // Check if current participant is YouTube
        const isCurrentParticipantYouTube
      = participant?.isFakeParticipant
      && participant?.name === YOUTUBE_PLAYER_PARTICIPANT_NAME;
        const isYouTubeActive
      = isYouTubePlaying(this.props) || isCurrentParticipantYouTube;

        // Hide SharedVideo when in PiP and YouTube is active (show avatar instead)
        const shouldShowSharedVideo = !(
            _isPictureInPictureVisible && isYouTubeActive
        );

        const nopointerEvents = !_isModerator && _isVisible ? { cursor: 'not-allowed' } : {};

        // Show avatar when in PiP and YouTube is active
        const shouldShowAvatarInPiP = _isPictureInPictureVisible && isYouTubeActive;

        const _dsObjectFit = { objectFit: this.props._stageObjectFit || objectFit || 'cover' };
        const isLocalVideoOnStage = Boolean(this.props.isLocal || participant?.local);
        const effectiveParticipantId = _participantId || participant?.id || _participants?.find(p => p?.local)?.id;
        const isStageMirrored = Boolean(
            isLocalVideoOnStage
            && !isLocalParticipantScreenSharing
            && _localFlipX
        );
        const largeVideoStyle = {
            ..._dsObjectFit,
            transform: isStageMirrored ? 'scaleX(-1)' : 'none'
        };
        const _dsOpacity = { opacity: shouldShowSharedVideo ? 1 : 0 };
        const style1 = { display: 'none' };

        return (
            <>
                <div
                    className = { className }
                    id = 'largeVideoContainer'
                    onMouseEnter = { this._onMouseEnter }
                    onMouseLeave = { this._onMouseLeave }
                    style = { style }>
                    {!isLocalParticipantScreenSharing && (
                        <LargeVideoDisplayName
                            _designation = { participant?.designation || _designation }
                            _filmStripView = { _filmStripView }
                            _organization = { participant?.organization || _organization }
                            _participantName = { participantDisplayName }
                            _showAudioMutedIndicator = { _showAudioMutedIndicator }
                            audioLevel = { audioLevel }
                            isLocal = { participant?.local === true }
                            key = { `large-display-name-${_participantId}` }
                            onToggleAudioMute = { () => {
                                this.props.dispatch(
                                    muteLocal(!_showAudioMutedIndicator, MEDIA_TYPE.AUDIO)
                                );
                            } }
                            participant = { participant }
                            participantDisplayName = { participantDisplayName }
                            raisedHand = { raisedHand }
                            t = { t } />
                    )}
                    {this._renderContextMenuPortal(isLocalVideoOnStage, effectiveParticipantId)}
                    {/* <InviteMore /> */}
                    {/* <div id = 'sharedVideo'>
                        <div id = 'sharedVideoIFrame' />
                    </div> */}
                    {/* <EtherpadManager /> */}
                    {/* Always render SharedVideo but hide when in PiP with YouTube */}

                    <div style = { _dsOpacity }>
                        <SharedVideo />
                    </div>
                    {(_isCustomParticipantVisible || _isCustomParticipantEveryone)
            && <GenericIFrameManager />
                    }
                    <div className = 'avatar-name-container' />
                    {/* <Watermarks /> */}
                    {/* <ConnectionLostOverlay
                        _participant = { participant }
                        filmStripView = { _filmStripView } /> */}
                    <ReactionAnimations />
                    {this._renderGif()}
                    <div
                        className = 'dominantSpeakerParent'
                        style = { isLocalParticipantScreenSharing ? style1 : {} }>
                        <div
                            id = 'dominantSpeaker'
                            style = {{
                                ...dominantSpeakerStyle,
                                ...shouldShowAvatarInPiP ? { display: 'block' } : {}
                            }}>
                            <div className = 'dynamic-shadow' />
                            <div id = 'dominantSpeakerAvatarContainer' />
                            {isLocalParticipantScreenSharing
              && <span>{t('dialog.screenSharingLocalUser')}</span>
                            }
                        </div>
                    </div>
                    {participant && !participant.local && !_isCustomParticipantVisible && !_isCustomParticipantEveryone && <ConnectionLostOverlay
                        _participant = { participant }
                        filmStripView = { _filmStripView } />}
                    {isLocalParticipantScreenSharing && !this.state.showMyScreenAnyway
                        && <div className = 'screen-share-local-participant'>
                            <Avatar
                                participantId = { screenSharingParticipant?.id } />
                            <span>{t('dialog.screenSharingLocalUser')}</span>
                            {!_isPictureInPictureVisible && <div className = 'screen-presentation-parent'>
                                <div className = 'screen-presentation-buttons'>
                                    <button
                                        className = 'screen-presentation'
                                        onClick = { () => {
                                            this.setState({ showMyScreenAnyway: true });
                                            jitsiLocalStorage.setItem('show_screen_while_screensharing', 'true');
                                        } }>{t('virtualBackground.showMyScreen')}</button>
                                    <button
                                        className = 'screen-presentation'
                                        onClick = { () => this.props.dispatch(startScreenShareFlow(false)) }>{t('toolbar.stopScreenSharing')}</button>
                                </div>
                                <span>{t('videothumbnail.infiniteScreenMirror')}</span>
                            </div>}
                        </div>
                    }
                    {isLocalParticipantScreenSharing && this.state.showMyScreenAnyway && !_isPictureInPictureVisible
                        && <div
                            className = 'screen-presentation-revert-wrapper'
                            style = {{
                                position: 'absolute',
                                top: '20px',
                                right: '20px',
                                zIndex: 10
                            }}>
                            <button
                                className = 'screen-presentation'
                                onClick = { () => {
                                    this.setState({ showMyScreenAnyway: false });
                                    jitsiLocalStorage.setItem('show_screen_while_screensharing', 'false');
                                } }
                                style = {{
                                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                    color: '#fff',
                                    padding: '8px 16px',
                                    borderRadius: '6px',
                                    border: '1px solid rgba(255, 255, 255, 0.3)',
                                    cursor: 'pointer',
                                    fontSize: '12px',
                                    fontWeight: 600
                                }}>
                                {t('toolbar.hideMyScreen', 'Hide My Screen')}
                            </button>
                        </div>
                    }
                    <div id = 'remotePresenceMessage' />
                    {/* <span id = 'remoteConnectionMessage' /> */}
                    <div id = 'largeVideoElementsContainer'>
                        <div
                            id = 'largeVideoBackgroundContainer'
                            onClick = { this._onHandleHeaderFooter } />

                        {/*
             * FIXME: the architecture of elements related to the large
             * video and the naming. The background is not part of
             * largeVideoWrapper because we are controlling the size of
             * the video through largeVideoWrapper. That's why we need
             * another container for the background and the
             * largeVideoWrapper in order to hide/show them.
             */}
                        {/* <div id = 'largeVideoWrapper'> */}
                        {
                            <PrismaZoom
                                doubleClickZoom = { DOUBLE_CLICK_VIEW_ZOOM }
                                id = 'largeVideoWrapper'
                                maxZoom = { CAMERA_ZOOM_MAX }
                                onPinch = { this.props._captureZoomType ? this._onCapturePinch : undefined }
                                onPinchEnd = { this.props._captureZoomType ? this._onCapturePinchEnd : undefined }
                                onTransformChange = { this._onTransformChange }
                                onZoomChange = { this._onZoomChange }
                                ref = { this.prismaZoomRef }
                                style = {{
                                    width: '100%',
                                    height: '100%',
                                    opacity: this.state.showMyScreenAnyway ? '' : participant?.isFakeParticipant === 'LocalScreenShare' ? '0.1' : ''
                                }}>
                                <LargeVideoElement
                                    autoPlay = { !this.props._noAutoPlayVideo }
                                    id = 'largeVideo'
                                    muted = { true }
                                    playsInline = { true }
                                    style = { largeVideoStyle }
                                    videoRef = { this.mainVideoRef } />
                            </PrismaZoom>
                        }
                        {/* {this._renderGif()} */}
                        {/* </div> */}
                    </div>
                    <ScreenAnnotationOverlay />
                    {this.props._filmStripView !== LAYOUTS.TILE_VIEW && (
                        <Labels
                            _showAudioMutedIndicator = { _showAudioMutedIndicator }
                            _showVideoMutedIndicator = { _showVideoMutedIndicator }
                            dispatch = { this.props.dispatch }
                            participantId = { this.props._participantId }
                            screenSize = { this.props._screenSize } />
                    )}
                    {this.props._filmStripView === LAYOUTS.STAGE_VIEW
            && !participant?.local
            && !_iAmRecorder && (
                        <DraggableResizable
                            className = 'small-video-stage-view-draggable'
                            draggable = { true }
                            initialPosition = {{
                                x: 90,
                                y: 10
                            }}
                            initialSize = {{
                                width: 300,
                                height: 170
                            }}
                            maxSize = {{
                                width: 600,
                                height: 400
                            }}
                            minSize = {{
                                width: 150,
                                height: 80
                            }}
                            resizable = { true }>
                            <Thumbnail
                                isLocal = { true }
                                singlevideo = { true } />
                        </DraggableResizable>
                    )}
                    {!(
                        this.props._filmStripView === LAYOUTS.TILE_VIEW
            && _conferenceView === 'float'
                    ) && (
                        <div
                            className = 'largevideo-toolbox'
                            onClick = { this._onHandleHeaderFooter }
                            style = { nopointerEvents }
                            title = { !_isModerator && _isVisible ? t('toolbar.accessibilityLabel.pointerBlockIframe') : '' }>
                            <Toolbox
                                _showVideoMutedIndicator = { _showVideoMutedIndicator }
                                hview = { _conferenceView === 'float' ? 'float' : null }
                                largevideo = { true } />
                        </div>
                    )}
                    {/* {this.props._filmStripView == 'Tile' ? <div style={{ display: 'flex' }}><Filmstrip _filmStripView={this.props._filmStripView} /></div> : null} */}
                    {showLoading && (
                        <div style = { LOADING_OVERLAY_STYLE }>
                            <div style = { LOADING_TEXT_STYLE }>Loading...</div>
                        </div>
                    )}
                </div>
                {/* {!_isParticipantsPaneVisible && <KnockingParticipantList />} */}
                {/* {filmStripView === 'Title' && <Filmstrip />} */}
                {/* {<Labels dispatch={this.props.dispatch} />} */}
            </>
        );
    }

    /**
   * Creates the custom styles object.
   *
   * @private
   * @returns {Object}
   */
    _getCustomSyles() {
        const styles = {};
        const {
            _customBackgroundImageUrl,
            _participantId,
            _participants,
            _shouldDisplayTileView,
            _screenshareOwnerId
        } = this.props;

        const participant = _participants.find(
      p => p.id === (_screenshareOwnerId || _participantId)
        );
        const participantColor = _shouldDisplayTileView
            ? participant?.color
            : 'transparent';

        styles.background = participantColor;
        if (_customBackgroundImageUrl) {
            styles.backgroundImage = `url(${_customBackgroundImageUrl})`;
            styles.backgroundSize = 'cover';
            styles.position = 'relative';
        }

        return styles;
    }

    /**
   * Renders GIF.
   *
   * @returns {Component}
   */
    _renderGif() {
        const { _gifSrc } = this.props;

        return (
            _gifSrc && (
                <div className = { 'gif-container' }>
                    <img
                        alt = 'GIF'
                        src = { _gifSrc } />
                </div>
            )
        );
    }

    /**
   * Renders the context menu using createPortal for proper z-index handling.
   *
   * @param {boolean} isLocal - Whether the participant is local.
   * @param {string} participantId - The participant ID.
   * @returns {React.ReactPortal|null} The context menu portal.
   */
    _renderContextMenuPortal(isLocal, participantId) {
    // Get or create portal container for context menus
        let portalContainer = document.getElementById('context-menu-portal');

        if (!portalContainer) {
            portalContainer = document.createElement('div');
            portalContainer.id = 'context-menu-portal';
            document.body.appendChild(portalContainer);
        }

        const contextMenu = isLocal ? (
            <LocalVideoContextMenu
                participantId = { participantId }
                setFlipX = { val => {
                    const localVideo = VideoLayout.getSmallVideo(participantId);

                    if (localVideo) {
                        localVideo.setFlipX(val);
                    } else {
                        VideoLayout.setLocalFlipX(val);
                    }

                    if (this.mainVideoRef.current) {
                        this.mainVideoRef.current.style.transform = val ? 'scaleX(-1)' : 'none';
                    }
                } }
                showParticipantName = { true }
                videoSpanId = { 'largeVideoContainer' } />
        ) : (
            <RemoteVideoContextMenu
                participantId = { participantId }
                showParticipantName = { true }
                videoSpanId = { 'largeVideoContainer' } />
        );

        return createPortal(contextMenu, portalContainer);
    }
}

/**
 * Maps (parts of) the Redux state to the associated LargeVideo props.
 *
 * @param {Object} state - The Redux state.
 * @private
 * @returns {Props}
 */
function _mapStateToProps(state) {
    const {
        mhConferenceJoined,
        isCustomParticipantVisible,
        isCustomParticipantEveryone,
        objectFit
    } = state['features/base/conference'];
    const { iAmRecorder } = state['features/base/config'];
    const testingConfig = state['features/base/config'].testing;
    const { userDetails } = state['features/base/mtApi'];
    const { backgroundColor, backgroundImageUrl }
    = state['features/dynamic-branding'];
    const { zoomin, zoomout } = state['features/toolbox'];
    const { isOpen: isChatOpen } = state['features/chat'];
    const { hideHeaderFooter } = state['features/filmstrip'];
    let { participantId } = state['features/large-video'];
    const audienceStageOnlyModeActive = isAudienceStageOnlyModeActive(state);

    // Hidden tiles are dropped here too, not just where the large-video
    // participant is elected: the re-election fallbacks below pick out of this
    // list, and they run whenever the selected id goes stale.
    const _participants = (audienceStageOnlyModeActive
        ? getWebinarAudienceLargeVideoParticipants(state)
        : state['features/base/participants'] || [])
        .filter(p => !isPresenterHiddenForLocal(state, p));
    const tracks = state['features/base/tracks'];
    const visitorMode = iAmVisitor(state);

    // Validate that the current participantId actually exists in the participants list.
    // If it doesn't (stale ID from debounced/throttled dispatch), re-elect a valid one.
    if (participantId && audienceStageOnlyModeActive && !_participants.some(p => p?.id === participantId)) {
        participantId = _participants[0]?.id;
    } else if (participantId && _participants.length > 0) {
        const existsInList = _participants.find(p => p?.id === participantId);

        if (!existsInList) {
            logger.debug(
                `LargeVideo mapStateToProps: participantId ${participantId} not found in participants, re-electing`
            );

            // Try to find a valid participant: pinned > moderatorPin > dominant speaker > last remote > any
            const pinned = _participants.find(p => p?.pinned);
            const modPin = _participants.find(p => p?.moderatorPin);
            const dominant = _participants.find(p => p?.dominantSpeaker && !p?.local);
            const lastRemote = _participants.filter(p => !p?.local && !p?.botType).pop();
            const fallback = pinned || modPin || dominant || lastRemote
                || _participants.find(p => !p?.botType) || _participants[0];

            if (fallback) {
                participantId = fallback.id;
            }
        }
    }

    // A visitor has no camera to put on stage. The local id is often still
    // selected from before the vnode redirect — that leaves stage/vertical blank.
    if (visitorMode) {
        const selected = _participants.find(p => p?.id === participantId);

        if (!participantId || selected?.local) {
            const remote = _participants.find(p => p?.pinned && !p?.local)
                || _participants.find(p => p?.moderatorPin && !p?.local)
                || _participants.find(p => p?.dominantSpeaker && !p?.local)
                || _participants.filter(p => !p?.local && !p?.botType).pop();

            if (remote) {
                participantId = remote.id;
            }
        }
    }

    const { gifUrl: gifSrc } = getGifForParticipant(state, participantId);
    const mode = getGifDisplayMode(state);
    const dominantSpeaker = _participants.find(
    p => p.dominantSpeaker
    );
    const videoTrackOnLargeVideo
    = getTrackByMediaTypeAndParticipant(
      tracks,
      MEDIA_TYPE.SCREENSHARE,
      participantId
    )
    || getTrackByMediaTypeAndParticipant(tracks, MEDIA_TYPE.VIDEO, participantId);
    let isVideoMuted = false;
    let isAudioMuted = false;
    let isScreenSharing = false;
    const participant = participantId
        ? getParticipantById(state, participantId)
        : getLocalParticipant(state);
    const pinnedLargeVideoParticipant
    = state[stateType.largeVideo]?.participantId;
    const screenSharingParticipantId = getScreenSharingParticipantId(participantId);
    const screenSharingParticipant = getParticipantById(state, screenSharingParticipantId);

    if (participant?.local) {
        isVideoMuted = isLocalTrackMuted(tracks, MEDIA_TYPE.VIDEO);
        isAudioMuted = isLocalTrackMuted(tracks, MEDIA_TYPE.AUDIO);
    } else if (participant && !participant.isFakeParticipant) {
    // remote participants excluding shared video
        const screenShareTrack = getTrackByMediaTypeAndParticipant(
            tracks,
            MEDIA_TYPE.SCREENSHARE,
            participantId
        );

        isScreenSharing = Boolean(screenShareTrack && !screenShareTrack.muted);
        isVideoMuted = isRemoteTrackMuted(tracks, MEDIA_TYPE.VIDEO, participantId);
        isAudioMuted = isRemoteTrackMuted(tracks, MEDIA_TYPE.AUDIO, participantId);
    }
    const localParticipant = getLocalParticipant(state);
    const isLocal = participantId === localParticipant?.id;
    const PIPDisabled = state['features/base/config'].disableAutoPiP;

    // The video on stage and who owns it. In mobile browsers a pinch on the
    // local user's own video zooms the capture; desktop browsers share the
    // view zoom of it instead, and receive the same from remote owners.
    const { screenshare: screenshareMedia, video: videoMedia } = state['features/base/media'];
    const stageVideo = getStageVideo(state);
    const mobile = isMobileBrowser();
    const captureZoomType = mobile && stageVideo?.local ? stageVideo.videoType : undefined;
    const isScreenShareOnStage = participant?.isFakeParticipant === 'RemoteScreenShare'
        || participant?.isFakeParticipant === 'LocalScreenShare';
    const ownerId = participant?.isFakeParticipant
        ? getScreenSharingParticipantId(participantId) || getVirtualScreenshareParticipantOwnerId(participantId)
        : participantId;
    const ownerParticipant = ownerId ? getParticipantById(state, ownerId) : null;
    const isStageParticipantModeratorPinned = Boolean(
        participant?.moderatorPin || ownerParticipant?.moderatorPin
    );
    const interfaceConfig = state['features/base/config'].interfaceConfig;

    // const isScreenShareParticipantsIds = getScreenshareParticipantIds(state);
    // const isLocalParticipantScreenSharing = isScreenShareParticipantsIds.find(id => id === localParticipant.id);

    const _audioTrack = isLocal
        ? getLocalAudioTrack(state['features/base/tracks'])
        : getTrackByMediaTypeAndParticipant(
        state['features/base/tracks'],
        MEDIA_TYPE.AUDIO,
        participantId
        );
    const desktopApp = state['features/base/mtApi'].desktopapp;
    const isDeskTop = interfaceConfig.ENABLE_DESKTOP_DEEPLINK === false && desktopApp === 1;

    // Always enable autoplay for recorders, even if noAutoPlayVideo is set for testing
    // This ensures that recorders can see video content when they enter meetings
    const shouldDisableAutoPlay = testingConfig?.noAutoPlayVideo && !iAmRecorder;
    const rawDialog = state['features/base/dialog'].rawDialog;
    const { visible } = state['features/genericiframe'];

    // Check if local user is sharing YouTube
    const { ownerId: ytOwnerId } = state['features/shared-video'];
    const _isLocalUserSharingYouTube
    = ytOwnerId === localParticipant?.id && isYouTubePlaying(state);

    return {
        _conference: getCurrentConference(state),
        _iAmRecorder: iAmRecorder,
        _iAmVisitor: visitorMode,
        _localParticipant: localParticipant,
        _customBackgroundColor: backgroundColor,
        _customBackgroundImageUrl: backgroundImageUrl,
        _isChatOpen: isChatOpen,
        _noAutoPlayVideo: shouldDisableAutoPlay,
        _participantName: getParticipantDisplayName(state, participantId),
        _clickbutton: zoomin,
        _isVisible: visible,
        _isModerator: isLocalParticipantModerator(state),
        _isStageParticipantModeratorPinned: isStageParticipantModeratorPinned,
        _stageParticipant: participant,
        _clickbutton1: zoomout,
        interfaceConfig,
        _gifSrc: mode === 'chat' ? null : gifSrc,
        _isPictureInPictureVisible: getPictureInPictureModeConfig(state),
        _shouldDisplayTileView: !shouldDisplayTileView(state),
        _videoTrack: videoTrackOnLargeVideo,
        _participantId: participantId,
        _participants,
        pinnedLargeVideoParticipant,
        _showAudioMutedIndicator: isAudioMuted && !participant?.isFakeParticipant,
        _showVideoMutedIndicator: isVideoMuted && !participant?.isFakeParticipant,
        _showScreenShareIndicator: isScreenSharing,
        userDisabledAutoPiP: PIPDisabled,
        _dialog: Boolean(state['features/base/dialog'].component),
        rawDialog,
        isLocal,
        hideHeaderFooter,
        objectFit,
        dominantSpeakerId: dominantSpeaker?.id,
        _designation: participant?.designation,
        _organization: participant?.organization,
        userDetails,
        _screenshareOwnerId: participant?.isFakeParticipant && getScreenSharingParticipantId(participantId),
        _audioTrack,
        _isCustomParticipantVisible: isCustomParticipantVisible,
        _isCustomParticipantEveryone: isCustomParticipantEveryone,
        _isLocalUserSharingYouTube,
        isLocalParticipantScreenSharing: participant?.isFakeParticipant === 'LocalScreenShare',
        _mhConferenceJoined: mhConferenceJoined,
        isDesktop: isDeskTop,
        screenSharingParticipant,
        _cameraZoom: videoMedia?.cameraZoom ?? CAMERA_ZOOM_MIN,
        _captureZoomType: captureZoomType,
        _localFlipX: state['features/base/settings']?.localFlipX !== false,
        _localVideoZoom: stageVideo?.local && !mobile
            ? getLocalVideoZoom(state, stageVideo.videoType)
            : undefined,
        _remoteVideoZoom: stageVideo
            ? !stageVideo.local || isStageParticipantModeratorPinned
                ? getRemoteVideoZoom(state, stageVideo.participantId, stageVideo.videoType)
                : undefined
            : undefined,
        _requestedViewZoom: getRequestedViewZoom(state),
        _screenshareZoom: screenshareMedia?.zoom ?? CAMERA_ZOOM_MIN,
        _localZoomShareApproved: getLocalZoomShareApproved(state),
        _moderatorZoomShareApproved: getModeratorZoomShareApproved(state),
        _stageObjectFit: isScreenShareOnStage ? 'contain' : objectFit || 'cover',
        _stageVideo: stageVideo
    };
}

const _mapDispatchToProps = {
    _fetchCustomBrandingData: fetchCustomBrandingData,
    _setPictureInPictureModeConfig: setPictureInPictureModeConfig,
    _pinParticipant: () => pinParticipant(null)
};


export default connect(_mapStateToProps, _mapDispatchToProps)(translate(LargeVideo));
